import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { selectAccountData } from '../redux/main/accountSlice';
import { selectColorsSchema } from '../redux/main/uiSlice';
import DynamicTitle from '../components/DynamicTitle';
import { getNodeApiUrl } from '../config/api.config';
import { AppsRoutes } from '../raadmin-template/Route/AuthRoutes';

/**
 * Componente per la creazione di manutenzioni tramite chat con l'MCP Server
 */
const MaintenancesAI = () => {
  const navigate = useNavigate();
  const accountData = useSelector(selectAccountData);
  const colorsSchema = useSelector(selectColorsSchema);
  
  // Stato per la chat
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [token, setToken] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  
  // Stato per la preview della manutenzione
  const [maintenancePreview, setMaintenancePreview] = useState({
    customer: null,
    system: null,
    systemTypes: [],
    date: null,
    notes: '',
    resolved: false
  });
  
  // Riferimento allo scrollable container dei messaggi
  const messagesEndRef = useRef(null);
  
  // Funzione per scorrere automaticamente in basso nella chat
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  
  // Funzione per recuperare i messaggi aggiornati di una conversazione dal server centrale
  // (chiamata UNA SOLA volta dopo ogni risposta dell'agente)
  const fetchConversationMessages = async (convId = null) => {
    // Usa l'ID passato come parametro o fallback sullo stato
    const conversationToFetch = convId || conversationId;
    if (!conversationToFetch) {
      console.log('Impossibile recuperare messaggi: ID conversazione mancante');
      return;
    }
    
    try {
      // Utilizziamo getNodeApiUrl come in tutte le altre chiamate API dell'applicazione
      const apiUrl = getNodeApiUrl(`/conversations/${conversationToFetch}`);
      
      // Recuperiamo il token di autenticazione dal localStorage come nelle altre pagine
      const authToken = localStorage.getItem('installamelo-auth-token');
      
      if (!authToken) {
        console.error('Token di autenticazione non trovato nel localStorage');
        toast.error('Sessione scaduta, effettua nuovamente il login');
        return;
      }
      
      console.log(`Recupero messaggi per conversazione ${conversationToFetch} da ${apiUrl} per account ${accountData._id}`);
      
      // Eseguiamo la chiamata API usando axios con header auth-token
      // e passiamo l'accountId come parametro nella query
      const response = await axios.get(apiUrl, {
        headers: {
          'Content-Type': 'application/json',
          'auth-token': authToken
        },
        params: {
          accountId: accountData._id // Aggiungiamo l'accountId come parametro di query
        }
      });
      
      if (response.data) {
        console.log('Risposta dal server centrale:', response.data);
        
        // Verifica se ci sono messaggi nella conversazione
        if (response.data.messages && response.data.messages.length > 0) {
          // Mappiamo i messaggi nel formato usato dal componente
          const formattedMessages = response.data.messages.map(msg => ({
            sender: msg.sender,
            content: msg.content,
            timestamp: msg.timestamp || new Date().toISOString(),
            // Preserviamo altri campi se presenti
            suggestions: msg.suggestions || [],
            context: msg.context,
            success: msg.success
          }));
          
          // Aggiorniamo i messaggi nell'interfaccia
          console.log('Aggiornamento messaggi con dati dal server centrale', formattedMessages);
          setMessages(formattedMessages);
          
          // Aggiorniamo anche il contesto se presente
          if (response.data.context) {
            updateMaintenancePreview({
              context: response.data.context
            });
          }
        }
      }
    } catch (error) {
      console.error('Errore nel recupero dei messaggi della conversazione:', error.message, error.response?.status, error.response?.data);
      toast.error(`Errore nel recupero dei messaggi: ${error.message}`);
    }
  };
  
  // Effetto per lo scroll automatico quando arrivano nuovi messaggi
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Effetto per ottenere il token MCP all'avvio
  useEffect(() => {
    const getMCPToken = async () => {
      try {
        setIsLoading(true);
        
        // Debug dello stato attuale
        console.log('Tentativo di connessione al server MCP, stato attuale:', {
          hasAccountData: !!accountData,
          accountId: accountData?._id,
          hasMcpConfig: !!accountData?.settings?.mcpServer,
          hasExistingToken: !!accountData?.settings?.mcpServer?.credentials?.token
        });
        
        // Verifica che accountData esista e contenga le configurazioni MCP Server
        if (!accountData?._id) {
          console.error('Dati account non disponibili');
          toast.error('Dati account non disponibili');
          setIsLoading(false);
          return;
        }
        
        if (!accountData?.settings?.mcpServer) {
          console.error('Configurazioni MCP Server non trovate');
          toast.error('Configurazioni MCP Server non trovate');
          setIsLoading(false);
          return;
        }
        
        const mcpConfig = accountData.settings.mcpServer;
        
        if (!mcpConfig.baseUrl) {
          console.error('URL MCP Server non trovato nelle configurazioni');
          toast.error('Configurazione MCP incompleta: URL mancante');
          setIsLoading(false);
          return;
        }
        
        console.log(`URL MCP Server configurato: ${mcpConfig.baseUrl}`);
        
        // Se già abbiamo un token nelle configurazioni dell'account, proviamo a usarlo direttamente
        if (mcpConfig.credentials?.token) {
          console.log('Token MCP trovato nelle configurazioni account, verifico se è ancora valido');
          const tokenExpiryDate = mcpConfig.credentials?.tokenExpiresAt ? new Date(mcpConfig.credentials.tokenExpiresAt) : null;
          
          // Se il token esiste ed è valido per almeno altri 5 minuti, lo usiamo direttamente
          if (tokenExpiryDate && tokenExpiryDate > new Date(Date.now() + 5 * 60000)) {
            console.log('Token MCP esistente è valido fino a:', tokenExpiryDate);
            setToken(mcpConfig.credentials.token);
            toast.success('Connessione al server MCP stabilita (token da cache)');
            
            // Aggiungi messaggio di benvenuto
            setMessages([{
              sender: 'agent',
              content: 'Ciao! Sono l\'assistente MCP per la creazione di manutenzioni. Come posso aiutarti oggi?',
              timestamp: new Date().toISOString()
            }]);
            
            setIsLoading(false);
            return;
          } else {
            console.log('Token MCP esistente è scaduto o sta per scadere, ne richiedo uno nuovo');
          }
        }
        
        console.log('Richiedo un nuovo token MCP al server Node');
        
        // Ottieni token MCP tramite API del server Node
        const response = await axios.post(
          getNodeApiUrl('/auth/mcp-token'), 
          { accountId: accountData._id },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('installamelo-auth-token')}`
            }
          }
        );
        
        console.log('Risposta dal server per richiesta token MCP:', response.data);
        
        // Gestione della risposta - Prima controlliamo il token diretto
        if (response.data && response.data.token) {
          console.log('Token ricevuto direttamente nella risposta API');
          setToken(response.data.token);
          toast.success('Connessione al server MCP stabilita');
          
          // Aggiungi messaggio di benvenuto
          setMessages([{
              sender: 'agent',
              content: 'Ciao! Sono l\'assistente MCP per la creazione di manutenzioni. Come posso aiutarti oggi?',
              timestamp: new Date().toISOString()
          }]);
        } 
        // Se abbiamo un messaggio di successo ma non il token diretto
        else if (response.data && response.data.message && 
                 (response.data.message.includes('Token MCP recuperato dalla cache') || 
                  response.data.message.includes('Nuovo token MCP ottenuto con successo'))) {
          
          console.log('Server indica che il token è stato generato/aggiornato con successo');
          console.log('Uso direttamente i dati dell\'account esistenti per ottenere il token');
          
          // Utilizziamo direttamente accountData che già contiene il token aggiornato
          // perché il server backend ha già aggiornato il database con il nuovo token
          if (accountData?.settings?.mcpServer?.credentials?.token) {
            const mcpToken = accountData.settings.mcpServer.credentials.token;
            console.log('Token trovato nei dati account esistenti:', mcpToken.substring(0, 10) + '...');
            
            setToken(mcpToken);
            toast.success('Connessione al server MCP stabilita');
            
            // Aggiungi messaggio di benvenuto
            setMessages([{
              sender: 'agent',
              content: 'Ciao! Sono l\'assistente MCP per la creazione di manutenzioni. Come posso aiutarti oggi?',
              timestamp: new Date().toISOString()
            }]);
          } else {
            // Caso molto raro: il backend ha salvato il token ma non lo troviamo nell'accountData
            // Facciamo una chiamata diretta per richiedere il token al server MCP
            console.log('Token non trovato nei dati account, richiedo token direttamente al server MCP');
            
            // Otteniamo il token direttamente dal server MCP
            try {
              const directResponse = await axios.post(
                `${mcpConfig.baseUrl}/auth/token`,
                {
                  accountErpId: accountData._id,
                  mcpKey: mcpConfig.credentials.mcpKey,
                  mcpSecret: mcpConfig.credentials.mcpSecret
                },
                { timeout: 15000 }
              );
              
              if (directResponse.data && directResponse.data.access_token) {
                console.log('Token ottenuto direttamente dal server MCP');
                setToken(directResponse.data.access_token);
                toast.success('Connessione al server MCP stabilita (diretto)');
                
                // Aggiungi messaggio di benvenuto
                setMessages([{
                  sender: 'agent',
                  content: 'Ciao! Sono l\'assistente MCP per la creazione di manutenzioni. Come posso aiutarti oggi?',
                  timestamp: new Date().toISOString()
                }]);
              } else {
                toast.error('Impossibile ottenere il token MCP dal server');
                console.error('Risposta server MCP senza token:', directResponse.data);
              }
            } catch (directError) {
              console.error('Errore nella richiesta diretta al server MCP:', directError);
              toast.error(`Errore di connessione diretta all'MCP: ${directError.message}`);
            }
          }
        } else {
          // Nessuna informazione sul token nella risposta
          console.error('Risposta senza informazioni sul token:', response.data);
          toast.error('Token MCP non ricevuto dal server');
        }
      } catch (error) {
        console.error('Errore durante la richiesta del token MCP:', error);
        if (error.response) {
          console.error('Dettagli errore:', {
            status: error.response.status,
            statusText: error.response.statusText,
            data: error.response.data
          });
        }
        toast.error(`Errore di connessione MCP: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (accountData?._id) {
      getMCPToken();
    }
  }, [accountData]);
  
  /**
   * Invia un messaggio al server MCP 
   * @param {string} message - Messaggio da inviare
   */
  const sendMessageToAgent = async (message) => {
    try {
      setIsLoading(true);
      
      // Aggiungi il messaggio utente alla chat
      setMessages(prev => [...prev, {
        sender: 'user',
        content: message,
        timestamp: new Date().toISOString()
      }]);
      
      // Verifica che il token sia disponibile
      if (!token) {
        toast.error('Token MCP non disponibile. Impossibile comunicare con il server.');
        return;
      }
      
      // Verifica che le configurazioni MCP esistano
      if (!accountData?.settings?.mcpServer) {
        toast.error('Configurazioni MCP Server non trovate');
        return;
      }
      
      const mcpConfig = accountData.settings.mcpServer;
      
      // Prepara il payload per la richiesta al formato atteso dall'endpoint /chat
      const payload = {
        message,
        user_id: accountData._id,
        user_role: 'admin', // Assumiamo un ruolo di default
        accountId: accountData._id, // Nome corretto del campo
        token: token,
        language: 'it'
      };
      
      // Aggiungi l'ID conversazione se presente
      if (conversationId) {
        payload.session_id = conversationId;
      }
      
      // URL base dell'MCP Server dalla configurazione dell'account
      const baseURL = mcpConfig.baseUrl;
      
      // Crea un client con il token di autenticazione e l'URL specifico dell'MCP
      const client = axios.create({
        baseURL: baseURL,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log(`Invio messaggio all'MCP Server:`, payload);
      
      // Chiama l'endpoint chat dell'MCP server
      const response = await client.post('/chat', payload);
      console.log('Risposta dall\'MCP Server:', response.data);
      
      // Estrai l'ID sessione dalla risposta per il prossimo turno
      if (response.data.session_id) {
        setConversationId(response.data.session_id);
      }
      
      // Ottieni la risposta dell'MCP server e gestisci le azioni
      const mcpResponse = response.data;
      
      // Aggiungi il messaggio dell'MCP server alla chat
      setMessages(prev => [...prev, {
        sender: 'agent',
        content: mcpResponse.response,
        timestamp: new Date().toISOString()
      }]);
      
      // Controlla se ci sono suggerimenti nelle azioni eseguite
      if (mcpResponse.actions_performed && mcpResponse.actions_performed.suggestions) {
        setSuggestions(mcpResponse.actions_performed.suggestions);
      } else {
        setSuggestions([]);
      }
      
      // Controlla se è stata creata una manutenzione e ottieni i dettagli
      if (mcpResponse.actions_performed && mcpResponse.actions_performed.maintenanceCreated) {
        const maintenanceData = mcpResponse.actions_performed.maintenanceCreated;
        setMaintenancePreview({
          ...maintenanceData,
          resolved: true,
          created: true
        });
        setShowMaintenanceConfirm(true); // Mostra la finestra di conferma
      }
      
      // Aggiorna la preview della manutenzione con tutti i dati disponibili
      updateMaintenancePreview(mcpResponse);
      
      // Se viene fornito un session_id ma non c'è una risposta, recupera i messaggi dal server
      if (mcpResponse.session_id && !mcpResponse.response) {
        console.log("Nessuna risposta diretta, recupero dal server per sessione", mcpResponse.session_id);
        fetchConversationMessages(mcpResponse.session_id);
      }
      
    } catch (error) {
      // Estrai il messaggio di errore specifico
      const errorMessage = error.response?.data?.detail || 
                        error.response?.data?.message ||
                        error.message || 
                        'Errore generico nella comunicazione con il server';
                        
      console.error('Errore durante l\'invio del messaggio:', errorMessage);
      
      // Aggiungi un solo messaggio di errore specifico alla chat
      setMessages(prev => [...prev, {
        sender: 'agent',
        content: `Mi dispiace, si è verificato un errore durante la comunicazione con il servizio di manutenzione: ${errorMessage}`,
        timestamp: new Date().toISOString(),
        isError: true
      }]);
      
      // Notifica l'errore con un toast
      toast.error(`Errore di comunicazione: ${errorMessage}`);
    } finally {
      setIsLoading(false);
      setInputMessage('');
    }
  };
  
  /**
   * Aggiorna l'anteprima della manutenzione in base alla risposta dell'agente
   * @param {Object} response - Risposta dall'agente
   */
  const updateMaintenancePreview = (response) => {
    // Controlla se il messaggio è in formato JSON
    let responseData = response;
    let parsedMessage = null;
    
    if (typeof response.message === 'string' && response.message.startsWith('{') && response.message.endsWith('}')) {
      try {
        parsedMessage = JSON.parse(response.message);
        // Integra i dati parsati nella risposta
        responseData = { ...response, ...parsedMessage };
        
        console.log('Messaggio JSON parsato con successo:', parsedMessage);
      } catch (e) {
        console.log('Errore nel parsing del messaggio JSON:', e);
      }
    }
    
    // Estrai i dati dal contesto e dalla risposta
    const contextData = responseData.context || {};
    const conversationContext = contextData.context || {};
    const responseContext = response.context || {};
    
    // Controlla anche nei dati dell'agente JSON
    const agentData = parsedMessage?.data || response.data || {};
    
    console.log('Aggiornamento preview con dati:', responseData);
    console.log('Context data:', contextData);
    console.log('Agent data:', agentData);
    
    // Aggiorna lo stato della preview
    setMaintenancePreview(prevState => {
      // Creazione di un nuovo stato integrando i dati ricevuti
      const newState = { ...prevState };
      
      // Aggiorna cliente se presente
      const customerId = responseData.customerId || 
                        conversationContext?.customerId ||
                        responseContext?.customerId ||
                        agentData.customerId ||
                        null;
      
      const customerLabel = responseData.customerLabel || 
                          conversationContext?.customerLabel ||
                          responseContext?.customerLabel || 
                          agentData.customerLabel || 
                          prevState.customer?.label;
      
      // Recupera indirizzo cliente
      const addressLabel = responseData.addressLabel || 
                          conversationContext?.addressLabel ||
                          responseContext?.addressLabel || 
                          agentData.addressLabel || 
                          prevState.address;
      
      if (customerId && customerLabel) {
        newState.customer = {
          id: customerId,
          label: customerLabel
        };
      }
      
      // Aggiorna l'indirizzo se disponibile
      if (addressLabel) {
        newState.address = addressLabel;
      }
      
      // Estrai l'ID del sistema da tutte le possibili fonti
      const systemId = responseData.systemId || 
                       conversationContext?.systemId ||
                       responseContext?.systemId ||
                       agentData.systemId ||
                       null;
      
      // Aggiorna impianto se presente
      if (systemId) {
        newState.system = {
          id: systemId,
          title: responseData.systemLabel || 
                 conversationContext?.systemLabel ||
                 responseContext?.systemLabel || 
                 agentData.systemLabel ||
                 // Estrai dalla stringa del messaggio, se presente
                 (responseData.message && responseData.message.includes("impianto '") ? 
                   responseData.message.split("impianto '")[1].split("'")[0] : 
                   prevState.system?.title || 'Impianto selezionato')
        };
      }
      
      // Aggiorna tipi di impianto con il nuovo formato systemTypes
      const systemTypes = responseData.systemTypes || 
                          conversationContext?.systemTypes ||
                          responseContext?.systemTypes ||
                          agentData.systemTypes;
      
      if (systemTypes && Array.isArray(systemTypes) && systemTypes.length > 0) {
        // Utilizziamo direttamente l'array systemTypes che contiene oggetti completi
        newState.systemTypes = systemTypes.map(type => ({
          typeId: type.typeId,
          label: type.typeLabel || 'Tipo impianto',
          testedDate: type.testedDate,
          expirationWarrantyDate: type.expirationWarrantyDate,
          _id: type._id
        }));
        
        console.log('Tipi di impianto aggiornati:', newState.systemTypes);
      } else {
        // Fallback al vecchio formato per retrocompatibilità
        const typeIds = responseData.systemTypeIds || 
                        conversationContext?.systemTypeIds ||
                        responseContext?.systemTypeIds ||
                        agentData.systemTypeIds || 
                        [];
        
        if (typeIds && typeIds.length > 0) {
          newState.systemTypes = typeIds.map(typeId => ({
            typeId,
            label: (responseData.systemTypeLabels || '').split(',')[typeIds.indexOf(typeId)]?.trim() || 'Tipo impianto'
          }));
        }
      }
      
      // Aggiorna data se presente
      const date = responseData.date || 
                   contextData.analysisInfo?.date ||
                   conversationContext?.date ||
                   responseContext?.date ||
                   agentData.date ||
                   prevState.date;
      
      if (date) {
        newState.date = date;
      }
      
      // Estrai eventuali note o descrizioni
      const notes = contextData.analysisInfo?.notes ||
                    conversationContext?.notes ||
                    responseContext?.notes ||
                    agentData.notes ||
                    prevState.notes;
      
      if (notes) {
        newState.notes = notes;
      }
      
      // Verifica se la manutenzione è stata creata con successo
      const maintenanceCreated = parsedMessage?.success === true && 
                               (parsedMessage.message?.includes('creata con successo') ||
                                parsedMessage.data?.maintenanceId);
      
      // Aggiorna lo stato di risoluzione e creazione
      newState.resolved = responseData.status === 200 ||
                         responseData.pendingAction === 'confirm_maintenance' ||
                         conversationContext?.pendingAction === 'confirm_maintenance' ||
                         responseContext?.pendingAction === 'confirm_maintenance' ||
                         responseData.success === true ||
                         maintenanceCreated;
      
      // Se la manutenzione è stata creata, aggiorna lo stato
      if (maintenanceCreated || agentData.maintenanceId) {
        newState.created = true;
        newState.maintenanceId = agentData.maintenanceId || 'ID_generato';
      }
      
      // Se c'è un'azione pendente, aggiungiamo questa informazione
      newState.pendingAction = responseData.pendingAction ||
                              conversationContext?.pendingAction ||
                              responseContext?.pendingAction ||
                              prevState.pendingAction;
      
      return newState;
    });
  };

  /**
   * Gestisce gli errori specifici nelle chiamate al server MCP
   * @param {Error} error - L'errore catturato
   * @param {string} userMessage - Il messaggio originale dell'utente
   * @returns {boolean} - True se l'errore è stato gestito come caso speciale
   */
  const handleAgentError = (error, userMessage) => {
    console.error('Errore nella comunicazione con il server MCP:', error);
    
    // Gestione specifica per errori 500 dal server che rappresentano completamento della conversazione
    if (error.response && error.response.status === 500) {
      const errorDetails = error.response.data?.detail || error.response.data?.message || error.message;
      console.error('Dettagli errore 500:', errorDetails);
      
      // Verifica se è l'errore specifico dello stato della conversazione
      if (errorDetails.includes('conversation completed') || errorDetails.includes('status: `completed`')) {
        console.log('Rilevato errore di conversazione completata');
        
        // Se l'ultimo messaggio dell'utente contiene una conferma
        if (userMessage.toLowerCase().includes('conferm') || 
            userMessage.toLowerCase().includes('sì') || 
            userMessage.toLowerCase().includes('si ')) {
          
          // Aggiungi un messaggio di successo alla chat
          setMessages(prev => [...prev, {
            sender: 'agent',
            content: 'Ho registrato la manutenzione con successo! Puoi visualizzarla nella sezione manutenzioni.',
            timestamp: new Date().toISOString(),
            success: true
          }]);
          
          // Aggiorna lo stato della preview
          setMaintenancePreview(prev => ({
            ...prev,
            resolved: true,
            created: true,
            maintenanceId: 'ID_' + new Date().getTime()
          }));
          
          // Mostra un toast di successo
          toast.success('Manutenzione creata con successo!');
          
          return true; // Errore gestito come caso speciale
        }
      }
    }
    
    // Per tutti gli altri errori, non gestiamo qui ma lasciamo gestire nel catch
    return false;
  };
  
  /**
   * Gestisce l'invio del messaggio quando l'utente preme Invio o il pulsante
   */
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;
    
    sendMessageToAgent(inputMessage.trim());
  };
  
  // Genera il colore di sfondo per i messaggi dell'agente
  const agentBgColor = colorsSchema?.primary ? `rgba(${parseInt(colorsSchema.primary.slice(1, 3), 16)}, ${parseInt(colorsSchema.primary.slice(3, 5), 16)}, ${parseInt(colorsSchema.primary.slice(5, 7), 16)}, 0.1)` : '#f0f4f8';
  
  // Stile per il messaggio dell'agente
  const agentMessageStyle = {
    backgroundColor: agentBgColor,
    borderLeft: `3px solid ${colorsSchema?.primary || '#1976d2'}`
  };
  
  // Stile per il messaggio dell'utente
  const userMessageStyle = {
    backgroundColor: '#e3f2fd',
    borderRight: `3px solid #2196f3`
  };
  
  // Stile per il pulsante di invio
  const sendButtonStyle = {
    backgroundColor: colorsSchema?.primary || '#1976d2',
    borderColor: colorsSchema?.primary || '#1976d2'
  };
  
  return (
    <div className="container-fluid px-0">
      <DynamicTitle title="Creazione Manutenzione con MCP" />
      
      <div className="row mb-3">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body">
              <h4 className="card-title mb-0">
                <i className="fas fa-robot mr-2"></i> Creazione Manutenzione con MCP Server
              </h4>
              <p className="text-muted">Usa il server MCP per creare una nuova manutenzione in modo semplice e conversazionale</p>
              
              {/* Stato di connessione */}
              <div className="d-flex align-items-center mt-2">
                <div className={`badge ${token ? 'bg-success' : 'bg-warning text-dark'} me-2`}>
                  <i className={`fas ${token ? 'fa-check-circle' : 'fa-spinner fa-spin'} me-1`}></i>
                  {token ? 'MCP Server connesso' : 'Connessione al server MCP in corso...'}
                </div>
                
                {isLoading && (
                  <div className="badge bg-info text-white">
                    <i className="fas fa-spinner fa-spin me-1"></i>
                    Elaborazione in corso
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="row">
        {/* Sezione Chat */}
        <div className="col-md-7 mb-4">
          <div className="card shadow-sm h-100">
            <div className="card-body d-flex flex-column" style={{ minHeight: '70vh' }}>
              <h5 className="card-title">Chat con l'Assistente MCP</h5>
              
              {/* Area messaggi */}
              <div 
                className="flex-grow-1 overflow-auto mb-3 p-3" 
                style={{ 
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  height: 'calc(70vh - 180px)'
                }}
              >
                {messages.length === 0 ? (
                  <div className="d-flex justify-content-center align-items-center h-100 text-muted">
                    <div className="text-center">
                      <i className="fas fa-comments fa-3x mb-3"></i>
                      <p>Inizia la conversazione con l'assistente</p>
                    </div>
                  </div>
                ) : (
                  <div className="list-group border-0">
                    {messages.map((msg, index) => (
                      <div 
                        key={index} 
                        className={`mb-3 p-3 rounded ${msg.sender === 'agent' ? '' : 'text-end'}`}
                        style={msg.sender === 'agent' ? agentMessageStyle : userMessageStyle}
                      >
                        <div className="d-flex align-items-center mb-1">
                          {msg.sender === 'agent' ? (
                            <>
                              <i className="fas fa-robot mr-2"></i>
                              <small className="text-muted">Assistente MCP</small>
                            </>
                          ) : (
                            <>
                              <div className="ms-auto"></div>
                              <small className="text-muted">Tu</small>
                              <i className="fas fa-user ml-2"></i>
                            </>
                          )}
                        </div>
                        <div className={msg.sender === 'agent' ? 'text-start' : 'text-end'}>
                          {msg.sender === 'agent' ? (
                            <div dangerouslySetInnerHTML={{
                              __html: msg.content
                                .replace(/\n/g, '<br>')
                                // Preserva la formattazione markdown per grassetto e corsivo
                                .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
                                .replace(/\*([^*]+)\*/g, '<em>$1</em>')
                                // Preserva le emoji
                                .replace(/([✀-➿]|[-]|�[�-�]|�[�-�]|[‑-⛿]|�[�-�])/g, '$1')
                            }} />
                          ) : (
                            msg.content
                          )}
                          
                          {/* Mostra suggerimenti se presenti */}
                          {msg.sender === 'agent' && msg.suggestions && msg.suggestions.length > 0 && (
                            <div className="mt-2">
                              <small className="text-muted d-block mb-1">Suggerimenti:</small>
                              <div className="d-flex flex-wrap">
                                {msg.suggestions.map((suggestion, i) => (
                                  <button 
                                    key={i}
                                    className="btn btn-sm btn-outline-primary m-1"
                                    onClick={() => {
                                      setInputMessage(suggestion);
                                      setTimeout(() => handleSubmit({ preventDefault: () => {} }), 100);
                                    }}
                                  >
                                    {suggestion}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Aggiungi informazioni sul prossimo passo se pendingAction è present */}
                          {msg.sender === 'agent' && maintenancePreview.pendingAction === 'confirm_maintenance' && (
                            <div className="alert alert-info mt-2" style={{ fontSize: '0.9rem' }}>
                              <i className="fas fa-info-circle mr-1"></i>
                              Per procedere, conferma la creazione della manutenzione o fornisci ulteriori dettagli.
                            </div>
                          )}
                        </div>
                        <div className="d-flex mt-1">
                          <small className="text-muted">
                            {new Date(msg.timestamp).toLocaleTimeString()}
                          </small>
                          
                          {/* Aggiungo icona di ripetizione per i messaggi dell'utente */}
                          {msg.sender === 'user' && (
                            <div className="ms-auto">
                              <button 
                                className="btn btn-sm text-muted p-0" 
                                style={{ fontSize: '0.8rem' }}
                                title="Ripeti questo messaggio"
                                onClick={() => {
                                  setInputMessage(msg.content);
                                  // Focus sull'input
                                  document.querySelector('input[type="text"]').focus();
                                }}
                              >
                                <i className="fas fa-redo"></i>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>
              
              {/* Form per invio messaggio */}
              <form onSubmit={handleSubmit}>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Scrivi un messaggio..."
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    disabled={isLoading || !token}
                  />
                  <button 
                    type="submit" 
                    className="btn" 
                    style={sendButtonStyle}
                    disabled={isLoading || !inputMessage.trim() || !token}
                  >
                    {isLoading ? (
                      <span>
                        <i className="fas fa-spinner fa-spin mr-2"></i> 
                        Invio...
                      </span>
                    ) : (
                      <span>
                        <i className="fas fa-paper-plane mr-2"></i>
                        Invia
                      </span>
                    )}
                  </button>
                </div>
                {!token && (
                  <small className="text-danger">
                    <i className="fas fa-exclamation-triangle mr-1"></i>
                    Connessione al server MCP in corso...
                  </small>
                )}
              </form>
            </div>
          </div>
        </div>
        
        {/* Sezione Preview Manutenzione */}
        <div className="col-md-5">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h5 className="card-title">Anteprima Manutenzione</h5>
              <p className="card-text text-muted mb-4">I dati vengono aggiornati durante la conversazione</p>
              
              <div className="p-3 border rounded mb-3">
                <h6 className="border-bottom pb-2 mb-3">
                  <i className="fas fa-info-circle mr-2"></i> Informazioni Principali
                </h6>
                
                <div className="row mb-3">
                  <div className="col-sm-4 text-muted">Cliente:</div>
                  <div className="col-sm-8 font-weight-bold">
                    {maintenancePreview.customer ? (
                      maintenancePreview.customer.label
                    ) : (
                      <span className="text-muted">Non ancora specificato</span>
                    )}
                  </div>
                </div>
                
                {/* Visualizzazione indirizzo */}
                {maintenancePreview.address && (
                  <div className="row mb-3">
                    <div className="col-sm-4 text-muted">Indirizzo:</div>
                    <div className="col-sm-8">
                      {maintenancePreview.address}
                    </div>
                  </div>
                )}
                
                <div className="row mb-3">
                  <div className="col-sm-4 text-muted">Impianto:</div>
                  <div className="col-sm-8 font-weight-bold">
                    {maintenancePreview.system ? (
                      maintenancePreview.system.title
                    ) : (
                      <span className="text-muted">Non ancora specificato</span>
                    )}
                  </div>
                </div>
                
                <div className="row mb-3">
                  <div className="col-sm-4 text-muted">Tipi impianto:</div>
                  <div className="col-sm-8">
                    {maintenancePreview.systemTypes.length > 0 ? (
                      <div>
                        {maintenancePreview.systemTypes.map((type, index) => (
                          <span 
                            key={index} 
                            className="badge rounded-pill" 
                            style={{ 
                              backgroundColor: colorsSchema?.secondary || '#f50057',
                              color: 'white',
                              margin: '0 3px 3px 0',
                            }}
                          >
                            {type.label}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted">Nessun tipo specificato</span>
                    )}
                  </div>
                </div>
                
                <div className="row mb-3">
                  <div className="col-sm-4 text-muted">Data pianificata:</div>
                  <div className="col-sm-8 font-weight-bold">
                    {maintenancePreview.date ? (
                      maintenancePreview.date
                    ) : (
                      <span className="text-muted">Non ancora specificata</span>
                    )}
                  </div>
                </div>
                
                {maintenancePreview.notes && (
                  <div className="row mb-3">
                    <div className="col-sm-4 text-muted">Note:</div>
                    <div className="col-sm-8">{maintenancePreview.notes}</div>
                  </div>
                )}
              </div>
              
              <div className="d-flex justify-content-between align-items-center mt-4">
                <div>
                  <span 
                    className={`badge ${maintenancePreview.resolved ? 'bg-success' : 'bg-warning text-dark'}`}
                  >
                    {maintenancePreview.resolved ? 'Pronto per la creazione' : 'In attesa di informazioni'}
                  </span>
                </div>
                
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-info"
                    style={{
                      backgroundColor: colorsSchema?.secondary || '#0dcaf0',
                      borderColor: colorsSchema?.secondary || '#0dcaf0',
                      color: 'white'
                    }}
                    onClick={() => navigate(AppsRoutes.MCP_SERVER_PAGE)}
                  >
                    <i className="fas fa-server mr-2"></i>
                    Test MCP Server
                  </button>
                  
                  <button
                    className="btn btn-primary"
                    style={sendButtonStyle}
                    disabled={!maintenancePreview.resolved}
                    onClick={() => {
                      if (maintenancePreview.pendingAction === 'confirm_maintenance') {
                        setInputMessage('Confermo la creazione della manutenzione');
                        setTimeout(() => handleSubmit({ preventDefault: () => {} }), 100);
                      }
                    }}
                  >
                    <i className="fas fa-save mr-2"></i>
                    {maintenancePreview.pendingAction === 'confirm_maintenance' ? 'Conferma Manutenzione' : 'Crea Manutenzione'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaintenancesAI;
